import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { PrepareRoutes } from '@core/framework/navigator.service';
import { ExceptionComponent } from '@core/exceptions/exception.component';
import { LoginGuard } from '@core/framework/login.guard';
import { UsersActivityStatusComponent } from '@ui/components/general/user-activity-status/usersactivitystatus.component';
import { NotificationsHistoryComponent } from '@ui/notifications/notifications-history/notificationshistory.component';
import { NotificationViewComponent } from '@ui/notifications/notifications-history/notification-view/notificationview.component';
import { EmptyLayoutComponent } from '@ui/layouts/layout/empty-layout/emptylayout.component';
import { MainLayoutComponent } from '@ui/layouts/layout/main-layout/mainlayout.component';
import { ComingSoonComponent } from '@ui/components/login/coming-soon/comingsoon.component';
import { LoginComponent } from '@ui/components/login/login/login.component';
import { LogoutComponent } from '@ui/components/login/logout/logout.component';
import { SessionExpireComponent } from '@ui/components/login/session-expire/sessionexpire.component';
import { PasswordChangeComponent } from '@ui/components/password/password-change/passwordchange.component';
import { PasswordChangeExpiredComponent } from '@ui/components/password/password-change-expired/passwordchangeexpired.component';
import { PasswordManageComponent } from '@ui/components/password/password-manage/passwordmanage.component';
import { PasswordRecoveryComponent } from '@ui/components/password/password-recovery/passwordrecovery.component';
import { PasswordWelcomeComponent } from '@ui/components/password/password-welcome/passwordwelcome.component';
import { LoginByOtpComponent } from '@ui/components/login/login-by-otp/login-by-otp.component';
import { AppConfigService } from '@configuration/app.config.service';
import { NotificationServerListComponent } from '@ui/notifications/notification-server/list/notification-server-list.component';
import { NotificationServerComponent } from '@ui/notifications/notification-server/notification-server.component';
import { NotificationTemplateListComponent } from '@m4u-angular/ui/notifications/notification-template/notification-template-list/notification-template-list.component';
import { NotificationTemplateComponent } from '@m4u-angular/ui/notifications/notification-template/notification-template.component';
import { SystemOptionsComponent } from '@ui/components/general/system-options/system-options.component';
import { MidStoreComponent } from '@core/mid/mid-store.component';
import {DemoButtonsComponent} from "@ui/demo/components/buttons/demo-buttons.component";
import {DemoLayoutComponent} from "@ui/demo/layouts/demo-layout/demo-layout.component";
import {DemoEditorsComponent} from "@ui/demo/components/editors/demo-editors.component";
import {DemoLayoutsComponent} from "@ui/demo/components/layouts/demo-layouts.component";
import {DemoSectionsComponent} from "@ui/demo/components/sections/demo-sections.component";
import {DemoCardsComponent} from "@ui/demo/components/cards/demo-cards.component";
import {DemoTabsComponent} from "@ui/demo/components/tabs/demo-tabs.component";
import {DemoGridsComponent} from "@ui/demo/components/grids/demo-grids.component";
import {DemoBottomSheetsComponent} from "@ui/demo/components/bottom-sheets/demo-bottom-sheets.component";
import { DemoPagesComponent } from "@ui/demo/components/pages/demo-pages.component";
import { HistoryViewComponent } from "@ui/components/general/history-view/historyview.component";
import {
    DemoPageToolbarButtonsComponent
} from "@ui/demo/components/pages/examples/toolbar-buttons/demo-page-toolbar-buttons.component";
import {
    DemoPageHeaderButtonsComponent
} from "@ui/demo/components/pages/examples/header-buttons/demo-page-header-buttons.component";
import {DemoSidebarsComponent} from "@ui/demo/components/sidebars/demo-sidebars.component";
import {DemoDialogsComponent} from "@ui/demo/components/dialogs/demo-dialogs.component";
import {DemoPipesComponent} from "@ui/demo/components/pipes/demo-pipes.component";
import {DemoComponentsComponent} from "@ui/demo/components/components/demo-components.component";
import {UserLoginHistoryComponent} from "@ui/components/general/user-login-history/user-login-history.component";
import { RequestHistoryListComponent } from '@ui/components/general/request-history/request-history-list/request-history-list.component';
import { RequestHistoryComponent } from '@ui/components/general/request-history/request-history.component';

const routes: Routes = [
    // Demo routes
    {
      path: 'demo', component: DemoLayoutComponent,
      children: [
          { path: 'buttons', component: DemoButtonsComponent },
          { path: 'editors', component: DemoEditorsComponent },
          { path: 'layouts', component: DemoLayoutsComponent },
          { path: 'components', component: DemoComponentsComponent },
          { path: 'sections', component: DemoSectionsComponent },
          { path: 'cards', component: DemoCardsComponent },
          { path: 'tabs', component: DemoTabsComponent },
          { path: 'grids', component: DemoGridsComponent },
          { path: 'page-examples', component: DemoPagesComponent },
          { path: 'page-examples/toolbar-buttons', component: DemoPageToolbarButtonsComponent },
          { path: 'page-examples/header-buttons', component: DemoPageHeaderButtonsComponent },
          { path: 'sidebars', component: DemoSidebarsComponent },
          { path: 'bottom-sheets', component: DemoBottomSheetsComponent },
          { path: 'dialogs', component: DemoDialogsComponent },
          { path: 'pipes', component: DemoPipesComponent }
      ]  
    },
    //System routes
    {
        path: 'system', component: EmptyLayoutComponent,
        children: [
            { path: 'login', component: LoginComponent },
            { path: 'login-otp', component: LoginByOtpComponent },
            { path: 'logout', component: LogoutComponent },
            { path: 'sessionexpired', component: SessionExpireComponent },
            { path: 'exception', component: ExceptionComponent },
            { path: 'comingsoon', component: ComingSoonComponent },
            { path: 'passwordchange', component: PasswordChangeComponent, canActivate: [LoginGuard] },
            { path: 'passwordchangeexpired', component: PasswordChangeExpiredComponent },
            { path: 'passwordrecovery', component: PasswordRecoveryComponent },
            { path: 'passwordwelcome', component: PasswordWelcomeComponent },
            { path: 'midstore', component: MidStoreComponent },
            { path: 'buttons', component: DemoButtonsComponent }
        ]
    },
    {
        path: 'pages', component: MainLayoutComponent,
        children: [
            //General routes
            ...PrepareRoutes([{ path: 'passwordmanage', component: PasswordManageComponent, canActivate: [LoginGuard], outlet: 'window' }]),
            {
                path: 'general',
                children: PrepareRoutes([
                    { path: 'usersactivitystatus', component: UsersActivityStatusComponent, data: { Menu: 'usersactivitystatus' }, canActivate: [LoginGuard] },
                    { path: 'notificationshistory', component: NotificationsHistoryComponent, data: { Menu: 'notificationshistory' }, canActivate: [LoginGuard] },
                    { path: 'notificationview/:EntryID', component: NotificationViewComponent, data: { Menu: 'notificationview' }, canActivate: [LoginGuard], outlet: 'window' },
                    { path: 'notification-servers', component: NotificationServerListComponent, data: { Menu: 'notification_servers' }, canActivate: [LoginGuard] },
                    { path: 'notification-server/:EntryID', component: NotificationServerComponent, data: { Menu: 'notification_server' }, canActivate: [LoginGuard] , outlet: 'window' },
                    { path: 'notification-templates', component: NotificationTemplateListComponent, data: { Menu: 'notification_templates' }, canActivate: [LoginGuard] },
                    { path: 'notification-template/:EntryID', component: NotificationTemplateComponent, data: { Menu: 'notification_template' }, canActivate: [LoginGuard], outlet: 'window' },
                    { path: 'system-options', component: SystemOptionsComponent, data: { Menu: 'system_options' }, canActivate: [LoginGuard] },
                    { path: 'user-login-history', component: UserLoginHistoryComponent, data: { Menu: 'user_login_history' }, canActivate: [LoginGuard] },
                    { path: 'requests-history', component: RequestHistoryListComponent, data: { Menu: 'request_history' }, canActivate: [LoginGuard] },
                    { path: 'request-history/:EntryID', component: RequestHistoryComponent, canActivate: [LoginGuard], outlet: 'window' },
                    { path: 'historyview', component: HistoryViewComponent, data: { Menu: 'historyview' }, canActivate: [LoginGuard] }
                ])
            },
            //Custom anonymous routes
            ...PrepareRoutes(AppConfigService.loadConfig().RoutesAnonymous),
            //Custom protected routes
            ...PrepareRoutes(AppConfigService.loadConfig().RoutesProtected)
        ]
    },
    //Custom routes with empty layout
    {
        path: 'pages', component: EmptyLayoutComponent,
        children: PrepareRoutes(AppConfigService.loadConfig().RoutesWithEmptyLayout)
    },
    //Redirect to login on entry or to home page
    {
        path: '',
        redirectTo: AppConfigService.loadConfig().OnGetHomePagePath() ? `pages/${PrepareRoutes([{ path: AppConfigService.loadConfig().OnGetHomePagePath()} ])[0].path}` : 'system/login',
        pathMatch: 'full'
    },
    {
        path: '**',
        component: ExceptionComponent,
        data: {
            MessageCode: 'msgRouteNotFound'
        }
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {
    onSameUrlNavigation: 'reload',
    relativeLinkResolution: 'legacy'
})],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
